import React, { useState, useRef } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { RiArrowGoBackFill } from "react-icons/ri";
import { Heart, Share2, ChevronLeft, ChevronRight, Play } from "lucide-react";

const ProductDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const product = location.state?.product;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Zoom functionality states
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  if (!product) {
    return (
      <div className="p-6">
        <p className="text-center text-gray-600">
          Product not found.{" "}
          <Link to="/" className="text-[#101b45] hover:underline">
            Go back
          </Link>
        </p>
      </div>
    );
  }

  const files = product.files || [];
  const currentFile = files[selectedImageIndex];
  const isCurrentVideo = currentFile?.file?.match(/\.(mp4|webm|ogg|mov)$/i);

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? files.length - 1 : prev - 1));
    setIsVideoPlaying(false);
    setIsZooming(false);
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev === files.length - 1 ? 0 : prev + 1));
    setIsVideoPlaying(false);
    setIsZooming(false);
  };

  const handleThumbnailClick = (index) => {
    setSelectedImageIndex(index);
    setIsVideoPlaying(false);
    setIsZooming(false);
  };

  const toggleVideoPlay = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const isFileVideo = (fileUrl) => {
    return fileUrl?.match(/\.(mp4|webm|ogg|mov)$/i);
  };

  // Image zoom handlers
  const handleMouseEnter = () => {
    if (!isCurrentVideo) {
      setIsZooming(true);
    }
  };

  const handleMouseLeave = () => {
    setIsZooming(false);
  };

  const handleMouseMove = (e) => {
    if (!isZooming || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setZoomPosition({ x, y });
  };

  const handleAddToCart = () => {
    const existingCart = JSON.parse(localStorage.getItem("cart")) || [];
    const productToAdd = {
      ...product,
      quantity: product.min_order_quantity || 1,
    };

    // Check if product already exists
    const index = existingCart.findIndex((p) => p.id === product.id);
    if (index > -1) {
      existingCart[index].quantity += productToAdd.quantity; // increment quantity
    } else {
      existingCart.push(productToAdd);
    }

    localStorage.setItem("cart", JSON.stringify(existingCart));
  };

  return (
    <div className="product-details-page py-6 max-w-7xl mx-auto">
      {/* Back Button */}
      <Link
        to="/"
        className="inline-block mb-6 text-gray-700 hover:text-[#101b45] transition"
      >
        <div className="flex gap-2 items-center">
          <RiArrowGoBackFill className="text-xl" />
          <span className="font-medium">Back to Products</span>
        </div>
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side - Images */}
        <div className="flex gap-4">
          {/* Thumbnail Column */}
          <div className="flex flex-col gap-3 w-20">
            {files.map((file, index) => {
              const isVideo = isFileVideo(file.file);
              return (
                <button
                  key={file.id}
                  onClick={() => handleThumbnailClick(index)}
                  className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImageIndex === index
                      ? "border-black"
                      : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {isVideo ? (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <Play className="w-6 h-6 text-gray-600" />
                    </div>
                  ) : (
                    <img
                      src={file.file}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Main Image Display */}
          <div className="flex-1 relative bg-gray-50 rounded-xl overflow-hidden">
            {/* Wishlist & Share Buttons */}
            <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
              <button
                onClick={() => setIsWishlisted(!isWishlisted)}
                className="bg-white rounded-full p-3 shadow-md hover:shadow-lg transition"
              >
                <Heart
                  className={`w-5 h-5 ${
                    isWishlisted ? "fill-red-500 text-red-500" : "text-gray-700"
                  }`}
                />
              </button>
              <button className="bg-white rounded-full p-3 shadow-md hover:shadow-lg transition">
                <Share2 className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            {/* Navigation Arrows */}
            {files.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition z-10"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition z-10"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
              </>
            )}

            {/* Image/Video Display with Zoom */}
            <div
              className="w-full h-[500px] flex items-center justify-center p-8"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
            >
              {isCurrentVideo ? (
                <div className="relative w-full h-full">
                  <video
                    ref={videoRef}
                    src={currentFile.file}
                    className="w-full h-full object-contain"
                    loop
                    onClick={toggleVideoPlay}
                  />
                  {!isVideoPlaying && (
                    <button
                      onClick={toggleVideoPlay}
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20"
                    >
                      <div className="bg-white rounded-full p-4 shadow-lg">
                        <Play
                          className="w-10 h-10 text-gray-800"
                          fill="currentColor"
                        />
                      </div>
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative w-full h-full overflow-hidden">
                  <img
                    ref={imageRef}
                    src={currentFile?.file || "https://via.placeholder.com/500"}
                    alt={product.name}
                    className={`w-full h-full object-contain transition-transform duration-200 ${
                      isZooming ? "cursor-zoom-in" : ""
                    }`}
                    style={{
                      transform: isZooming ? "scale(2)" : "scale(1)",
                      transformOrigin: isZooming
                        ? `${zoomPosition.x}% ${zoomPosition.y}%`
                        : "center",
                    }}
                  />

                  {/* Zoom indicator */}
                  {isZooming && (
                    <div className="absolute top-4 left-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
                      🔍 Zoomed 2x
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Product Info */}
        <div className="space-y-6">
          <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>

          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
              {product.category?.name}
            </span>
          </div>

          <div className="border-t border-b py-4">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-gray-900">
                ${product.base_price}
              </span>
              <span className="text-gray-500 text-lg">
                {product.currency || "USD"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 text-sm mb-1">Stock Quantity</p>
              <p className="text-2xl font-semibold text-gray-900">
                {product.stock_quantity || 0}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-500 text-sm mb-1">Min Order</p>
              <p className="text-2xl font-semibold text-gray-900">
                {product.min_order_quantity || 0}
              </p>
            </div>
          </div>

          {product.description && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Description
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>
            </div>
          )}

          <div className="mt-3">
            <Link
              to="/checkout"
              state={{
                product: {
                  ...product,
                  quantity: product.min_order_quantity || 1,
                },
              }}
              className="cursor-pointer block"
            >
              <button
                onClick={handleAddToCart}
                className="w-full bg-[#101b45] text-white py-3 rounded-lg font-semibold text-lg hover:bg-[#101b45] transition shadow-md hover:shadow-lg"
              >
                Order
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
